import { action, computed, observable } from 'mobx';

import { IOrderConfig } from '@estee/elc-layouts';
import { ViewController } from '@estee/elc-service';
import { command, emit, events, serviceCommands } from '@estee/elc-service-bus';

import { AccountOverlay } from '~domain/entities/AccountOverlay';
import { AccountRepository } from '~repositories/AccountRepository';
import { AccountSidebarLoadedUseCase } from '~use-cases/AccountSidebarLoadedUseCase';
import { IMenuLink } from '~views/sidebar/menu/Menu';
import { sidebarSections } from '~views/sidebar/sections';
import { IShowcaseItem } from '~views/sidebar/showcase/IShowcase';

interface IAccountSidebarController {
    accountOverlay: AccountOverlay;
    accountRepository: AccountRepository;
}

class AccountSidebarController extends ViewController {
    private accountRepository: AccountRepository;
    @observable private accountOverlay: AccountOverlay;
    @observable private orderableConfig: IOrderConfig;

    constructor({ accountOverlay, accountRepository }: IAccountSidebarController) {
        super();
        this.accountOverlay = accountOverlay;
        this.accountRepository = accountRepository;
    }

    public loaded = async () => {
        await new AccountSidebarLoadedUseCase().execute();
    };

    @computed
    public get accountSidebarConfig() {
        const { showLoyalty } = this.accountRepository;
        const { [sidebarSections.loyalty]: loyaltyTier, ...remainingConfig } = this.orderableConfig;

        if (showLoyalty === false) {
            return remainingConfig;
        }

        return this.orderableConfig;
    }

    @action
    public setOrderableConfig = (orderableConfig: IOrderConfig) => {
        this.orderableConfig = orderableConfig;
    };

    public handleMenuLinkOnClick = (menuLinkData: IMenuLink) => {
        const { label } = menuLinkData;
        emit(events.ACCOUNT_SIDEBAR_MENU_LINK_CLICKED, { label });
    };

    public handleShowcaseOnClick = (showcaseData: IShowcaseItem) => {
        const { title, showCaseKey } = showcaseData;
        const analyticsPayload = {
            item_title: title,
            key: showCaseKey
        };
        emit(events.ACCOUNT_SIDEBAR_SHOWCASE_LINK_CLICKED, analyticsPayload);
    };

    @action
    public hideOverlay = () => {
        void command(serviceCommands.ACCOUNT_OVERLAY_HIDE);
    };

    @computed public get data() {
        return {
            isOverlayVisible: this.accountOverlay.isOverlayVisible
        };
    }
}

export default AccountSidebarController;
